import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button, Collapse, Drawer, Input, InputNumber, Popconfirm, Select, Skeleton, Switch, Tooltip } from 'antd'
import {
    AppstoreOutlined,
    BankOutlined,
    CarOutlined,
    CheckCircleFilled,
    ClockCircleOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    DisconnectOutlined,
    GiftOutlined,
    LockOutlined,
    MailOutlined,
    MessageOutlined,
    PlusOutlined,
    SearchOutlined,
    SettingOutlined,
    ShopOutlined,
    TeamOutlined,
    ThunderboltFilled,
} from '@ant-design/icons'
import { Titles } from '../../layouts/Titles'
import i from '../../translations/i'
import { getRateName } from '../../helpers/language.helper'
import __apps, { APP_GROUPS, AppGroup, appEvents, appGroup, AppInterface, appLocked, appName } from './__apps'
import './apps.less'
import './apps.less'

const GROUP_ICON: Record<AppGroup, React.ReactNode> = {
    sync: <ShopOutlined />,
    crm: <TeamOutlined />,
    payment: <CreditCardOutlined />,
    delivery: <CarOutlined />,
    bonus: <GiftOutlined />,
    auth: <MessageOutlined />,
    emails: <MailOutlined />,
    company: <BankOutlined />,
    search: <SearchOutlined />,
    other: <AppstoreOutlined />,
    soon: <ClockCircleOutlined />,
}

const groupLabel = (group: AppGroup) => i.t(`appsStore:groups.${group}`)
const eventLabel = (event: string) => i.t(`def:appEvents.${event}`, { defaultValue: event })

// Ключи доступа — скрытым полем
const isSecret = (key: string) => /key|token|secret|password|pass/i.test(key)

// Логотип; если картинка не загрузилась — первые буквы названия на цветной плашке раздела
const AppLogo = ({ app, size = 'md' }: { app: any, size?: 'sm' | 'md' | 'lg' }) => {
    const [failed, setFailed] = useState(false)
    const name = appName(app)
    const initials = name.split(/[\s.\-_]+/).filter(word => /^[A-Za-zА-Яа-яЁё0-9]/.test(word)).slice(0, 2).map(word => word[0]).join('').toUpperCase()

    return (
        <span className={`as-logo as-logo--${size} as-tone--${appGroup(app)}${app.icon && !failed ? '' : ' as-logo--empty'}`}>
            {app.icon && !failed
                ? <img src={app.icon} alt="" loading="lazy" onError={() => setFailed(true)} />
                : initials || GROUP_ICON[appGroup(app)]
            }
        </span>
    )
}

const GroupLabel = ({ group }: { group: AppGroup }) => (
    <span className={`as-group as-tone--${group}`}>{GROUP_ICON[group]}{groupLabel(group)}</span>
)

// Возможности приложения — первые три, остальные числом с подсказкой
const Capabilities = ({ app, max = 3 }: { app: any, max?: number }) => {
    const events = appEvents(app)
    if (!events.length) return null

    const rest = events.slice(max)

    return (
        <div className="as-caps">
            {events.slice(0, max).map(event => <span key={event} className="as-cap">{eventLabel(event)}</span>)}
            {rest.length > 0 &&
                <Tooltip title={rest.map(eventLabel).join(', ')}>
                    <span className="as-cap as-cap--more">{i.t('appsStore:moreCaps', { count: rest.length })}</span>
                </Tooltip>
            }
        </div>
    )
}

const openable = (app: AppInterface) => appGroup(app) !== 'soon' && !appLocked(app)

const cardProps = (app: AppInterface) => openable(app)
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: (e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('a, button')) return
            __apps.open(app.id)
        },
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && e.target === e.currentTarget) __apps.open(app.id)
        },
    }
    : {}

// Карточка каталога
const AppCard = ({ app }: { app: AppInterface }) => {
    const group = appGroup(app)
    const soon = group === 'soon'
    const locked = !soon && appLocked(app)
    const plan = getRateName(Number(app.min_rate))

    return (
        <article
            className={`as-card${soon ? ' as-card--soon' : ''}${locked ? ' as-card--locked' : ''}`}
            {...cardProps(app)}
        >
            <div className="as-card__top">
                <AppLogo app={app} />
                {soon && <span className="as-badge as-badge--soon"><ClockCircleOutlined />{i.t('appsStore:soon')}</span>}
                {locked &&
                    <Tooltip title={i.t('appsStore:planHint', { plan })}>
                        <span className="as-badge as-badge--plan"><LockOutlined />{plan}</span>
                    </Tooltip>
                }
            </div>

            {/* Раздел не повторяем: карточка и так стоит под его заголовком */}
            <h3 className="as-card__name">{appName(app)}</h3>

            {app.config?.local?.annotation && <p className="as-card__text">{app.config.local.annotation}</p>}

            <Capabilities app={app} />

            <div className="as-card__footer">
                {soon && <span className="as-card__note">{i.t('appsStore:soonHint')}</span>}
                {locked && <>
                    <span className="as-card__note">{i.t('appsStore:planHint', { plan })}</span>
                    <Button type="link" size="small" href="/billing">{i.t('appsStore:changePlan')}</Button>
                </>}
                {!soon && !locked &&
                    <Button type="primary" icon={<ThunderboltFilled />} onClick={() => __apps.open(app.id)}>{i.t('appsStore:connect')}</Button>
                }
            </div>
        </article>
    )
}

// Подключённое приложение — компактная плитка со статусом
const InstalledCard = ({ app }: { app: AppInterface }) => (
    <article className="as-installed" {...cardProps(app)}>
        <AppLogo app={app} size="sm" />
        <div className="as-installed__body">
            <span className="as-installed__name">{appName(app)}</span>
            {app.enabled
                ? <span className="as-status as-status--on"><i />{i.t('appsStore:statusOn')}</span>
                : <span className="as-status"><i />{i.t('appsStore:statusOff')}</span>
            }
        </div>
        <Tooltip title={i.t('appsStore:configure')}>
            <Button shape="circle" icon={<SettingOutlined />} onClick={() => __apps.open(app.id)} />
        </Tooltip>
    </article>
)

// Витрина: заголовок, поиск и облако логотипов
const Hero = observer(() => {
    const logos = __apps.all.filter(app => app.icon).slice(0, 7)

    return (
        <section className="as-hero">
            <div className="as-hero__content">
                <span className="as-hero__eyebrow"><AppstoreOutlined />{i.t('appsStore:heroCount', { count: __apps.all.length })}</span>
                <h2 className="as-hero__title">{i.t('appsStore:heroTitle')}</h2>
                <p className="as-hero__text">{i.t('appsStore:heroText')}</p>
                <Input
                    size="large"
                    allowClear
                    className="as-hero__search"
                    prefix={<SearchOutlined />}
                    placeholder={i.t('appsStore:searchPlaceholder')}
                    value={__apps.search}
                    onChange={e => __apps.setSearch(e.target.value)}
                />
            </div>
            <div className="as-hero__cloud" aria-hidden>
                {logos.map((app, index) => (
                    <span key={app.id} className={`as-hero__bubble as-hero__bubble--${index}`}>
                        <AppLogo app={app} size="lg" />
                    </span>
                ))}
            </div>
        </section>
    )
})

const GroupChips = observer(() => {
    const counts = __apps.groupCounts
    const groups = APP_GROUPS.filter(group => counts[group])

    return (
        <div className="as-chips" role="tablist">
            <button
                type="button"
                role="tab"
                aria-selected={__apps.group === 'all'}
                className={`as-chip${__apps.group === 'all' ? ' as-chip--active' : ''}`}
                onClick={() => __apps.setGroup('all')}
            >
                <AppstoreOutlined />{i.t('appsStore:all')}<b>{__apps.all.length}</b>
            </button>
            {groups.map(group => (
                <button
                    key={group}
                    type="button"
                    role="tab"
                    aria-selected={__apps.group === group}
                    className={`as-chip${__apps.group === group ? ' as-chip--active' : ''}`}
                    onClick={() => __apps.setGroup(__apps.group === group ? 'all' : group)}
                >
                    {GROUP_ICON[group]}{groupLabel(group)}<b>{counts[group]}</b>
                </button>
            ))}
        </div>
    )
})

const SettingField = observer(({ field }: { field: any }) => {
    const label = __apps.settingLabel(field)
    const required = field.required === true || field.required === 'true'

    if (field.type === 'switch') {
        return (
            <label className="as-switch-row">
                <span>{label}</span>
                <Switch checked={!!field.value} onChange={value => __apps.setSetting(field, value)} />
            </label>
        )
    }

    return (
        <div className="as-field">
            <label className="as-field__label">{label}{required && <span className="as-field__required">*</span>}</label>

            {field.type === 'string' && (isSecret(field.key)
                ? <Input.Password value={field.value ?? ''} required={required} onChange={e => __apps.setSetting(field, e.target.value)} />
                : <Input value={field.value ?? ''} required={required} onChange={e => __apps.setSetting(field, e.target.value)} />
            )}

            {field.type === 'select' &&
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    value={field.value ?? undefined}
                    options={Array.isArray(field.options) ? field.options : []}
                    onChange={value => __apps.setSetting(field, value ?? null)}
                />
            }

            {field.type === 'select_array' &&
                <Select
                    mode="multiple"
                    allowClear
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    value={field.value ?? []}
                    options={Array.isArray(field.options) ? field.options : []}
                    onChange={value => {
                        __apps.setSetting(field, value)
                        if (field.save) __apps.saveApplication()
                    }}
                />
            }
        </div>
    )
})

const CustomFields = observer(() => {
    const fields: any[] = __apps.data?.fields ?? []

    return (
        <div className="as-custom">
            {fields.map((field: any, index: number) => (
                <div key={field.id ?? `new_${index}`} className="as-custom__item">
                    <div className="as-custom__grid">
                        <div className="as-field">
                            <label className="as-field__label">{i.t('def:apps.fieldKey')}<span className="as-field__required">*</span></label>
                            <Input value={field.field_key ?? ''} required onChange={e => __apps.setCustomField(field, 'field_key', e.target.value)} />
                        </div>
                        <div className="as-field">
                            <label className="as-field__label">{i.t('def:comment')}</label>
                            <Input value={field.comment ?? ''} onChange={e => __apps.setCustomField(field, 'comment', e.target.value)} />
                        </div>
                        <div className="as-field">
                            <label className="as-field__label">{i.t('def:apps.entity')}</label>
                            <Input value={field.entity_type ?? ''} onChange={e => __apps.setCustomField(field, 'entity_type', e.target.value)} />
                        </div>
                        <div className="as-field">
                            <label className="as-field__label">{i.t('def:apps.entityId')}</label>
                            <InputNumber style={{ width: '100%' }} value={field.entity_id ?? null} onChange={value => __apps.setCustomField(field, 'entity_id', value)} />
                        </div>
                        {(field.data && typeof field.data.value === 'string') &&
                            <div className="as-field as-custom__wide">
                                <label className="as-field__label">{i.t('def:apps.customValue')}</label>
                                <Input value={field.data.value} onChange={e => __apps.setCustomField(field, 'value', e.target.value)} />
                            </div>
                        }
                    </div>
                    <Tooltip title={i.t('def:actions.remove')}>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => __apps.deleteField(index)} />
                    </Tooltip>
                </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => __apps.addCustomField()}>{i.t('appsStore:addField')}</Button>
        </div>
    )
})

// Боковая панель приложения: описание, подключение, что передаём, пользовательские поля
const AppDrawer = observer(() => {
    const app = __apps.data
    const events = app ? appEvents(app) : []
    const width = Math.min(680, typeof window !== 'undefined' ? window.innerWidth : 680)

    const footer = app &&
        <div className="as-drawer__footer">
            <label className="as-drawer__enabled">
                <Switch checked={!!app.enabled} onChange={value => __apps.setEnabled(value)} />
                <span>
                    <b>{i.t('appsStore:enabled')}</b>
                    <small>{i.t('appsStore:enabledHint')}</small>
                </span>
            </label>
            <div className="as-drawer__actions">
                {app.installed &&
                    <Popconfirm
                        title={i.t('appsStore:disconnectConfirm', { name: appName(app) })}
                        okText={i.t('appsStore:disconnect')}
                        okButtonProps={{ danger: true }}
                        cancelText={i.t('def:no')}
                        onConfirm={() => __apps.delete(app.id)}
                    >
                        <Button danger icon={<DisconnectOutlined />}>{i.t('appsStore:disconnect')}</Button>
                    </Popconfirm>
                }
                <Button type="primary" htmlType="submit" form="app-settings" loading={__apps.saving}>
                    {app.installed ? i.t('def:actions.save') : i.t('appsStore:connect')}
                </Button>
            </div>
        </div>

    // Шапка приложения — в заголовке панели, рядом с крестиком
    const title = app &&
        <div className="as-detail__head">
            <AppLogo app={app} size="lg" />
            <div className="as-detail__title">
                <span className="as-detail__name">{appName(app)}</span>
                <div className="as-detail__meta">
                    <GroupLabel group={appGroup(app)} />
                    {app.installed && (app.enabled
                        ? <span className="as-status as-status--on"><i />{i.t('appsStore:statusOn')}</span>
                        : <span className="as-status"><i />{i.t('appsStore:statusOff')}</span>
                    )}
                    {Number(app.min_rate) > 0 && <span className="as-badge as-badge--plan-soft">{i.t('appsStore:planFrom', { plan: getRateName(Number(app.min_rate)) })}</span>}
                </div>
            </div>
        </div>

    return (
        <Drawer
            open={__apps.drawerOpen}
            width={width}
            placement="right"
            onClose={() => __apps.close()}
            afterOpenChange={open => !open && __apps.clear()}
            className="as-drawer"
            title={title}
            footer={footer}
            destroyOnClose
        >
            {!app
                ? <Skeleton active avatar paragraph={{ rows: 6 }} />
                : <form id="app-settings" onSubmit={e => __apps.save(e)} className="as-detail">
                    {(app.config?.local?.description || app.config?.local?.annotation) &&
                        <p className="as-detail__text">{app.config.local.description || app.config.local.annotation}</p>
                    }

                    <section className="as-section">
                        <h3 className="as-section__title"><SettingOutlined />{i.t('appsStore:settingsTitle')}</h3>

                        {__apps.pendingSettings > 0 &&
                            <Alert
                                type="info"
                                showIcon
                                className="as-section__alert"
                                message={i.t('appsStore:pendingHint', { count: __apps.pendingSettings, name: appName(app) })}
                            />
                        }

                        {__apps.settings.filter((field: any) => app.installed || !field.then).length
                            ? <div className="as-fields">
                                {__apps.settings
                                    .filter((field: any) => app.installed || !field.then)
                                    .map((field: any) => <SettingField key={field.key} field={field} />)
                                }
                            </div>
                            : <p className="as-muted">{i.t('appsStore:noSettings')}</p>
                        }
                    </section>

                    {events.length > 0 &&
                        <section className="as-section">
                            <h3 className="as-section__title"><CheckCircleFilled />{i.t('appsStore:eventsTitle')}</h3>
                            <p className="as-section__hint">{i.t('appsStore:eventsHint')}</p>
                            <div className="as-events">
                                {events.map(event => (
                                    <label key={event} className={`as-event${__apps.eventEnabled(event) ? ' as-event--on' : ''}`}>
                                        <span>{eventLabel(event)}</span>
                                        <Switch size="small" checked={__apps.eventEnabled(event)} onChange={value => __apps.setEvent(event, value)} />
                                    </label>
                                ))}
                            </div>
                        </section>
                    }

                    <Collapse
                        ghost
                        className="as-advanced"
                        items={[{
                            key: 'custom',
                            label: <span className="as-section__title as-section__title--inline">{i.t('appsStore:customTitle')}{(app.fields?.length ?? 0) > 0 && <b className="as-count">{app.fields.length}</b>}</span>,
                            children: <>
                                <p className="as-section__hint">{i.t('appsStore:customHint')}</p>
                                <CustomFields />
                            </>,
                        }]}
                    />
                </form>
            }
        </Drawer>
    )
})

export const Apps = observer(() => {
    const { id }: any = useParams()

    useEffect(() => {
        __apps.getList()
        if (Number(id) > 0) __apps.open(Number(id))
    }, [id])

    const installed = __apps.installedFiltered
    const sections = __apps.sections
    const nothing = !__apps.loading && installed.length === 0 && sections.length === 0

    return (
        <>
            <Titles title={i.t('def:apps.title')} breadcrumb={[{ title: i.t('def:apps.title') }]} />

            <div className="as-page">
                <Hero />
                <GroupChips />

                {__apps.loading && __apps.all.length === 0 &&
                    <div className="as-grid">
                        {[0, 1, 2, 3, 4, 5].map(index => <div key={index} className="as-card as-card--skeleton"><Skeleton active avatar paragraph={{ rows: 3 }} /></div>)}
                    </div>
                }

                {installed.length > 0 &&
                    <section className="as-block">
                        <div className="as-block__head">
                            <h3 className="as-block__title"><CheckCircleFilled className="as-block__icon--ok" />{i.t('appsStore:installedTitle')}</h3>
                            <span className="as-block__count">{installed.length}</span>
                        </div>
                        <div className="as-installed-grid">
                            {installed.map(app => <InstalledCard key={app.id} app={app} />)}
                        </div>
                    </section>
                }

                {sections.map(({ group, apps }) => (
                    <section key={group} className="as-block">
                        <div className="as-block__head">
                            <h3 className="as-block__title"><span className={`as-block__icon as-tone--${group}`}>{GROUP_ICON[group]}</span>{groupLabel(group)}</h3>
                            <span className="as-block__count">{apps.length}</span>
                        </div>
                        <div className="as-grid">
                            {apps.map(app => <AppCard key={app.id} app={app} />)}
                        </div>
                    </section>
                ))}

                {nothing &&
                    <div className="as-empty">
                        <span className="as-empty__icon"><SearchOutlined /></span>
                        <h3>{i.t('appsStore:empty')}</h3>
                        <p>{i.t('appsStore:emptyHint')}</p>
                        {__apps.filtered && <Button onClick={() => __apps.resetFilters()}>{i.t('appsStore:reset')}</Button>}
                    </div>
                }
            </div>

            <AppDrawer />
        </>
    )
})

// Прямая ссылка на приложение (/apps/:id) открывает витрину с его панелью
export const AppsDetail = Apps
